import axios from 'axios';
import API_BASE_URL from './config';
import logger from './utils/logger';

export const configureAxios = () => {
  // 1. Intercept Requests to add Access Token and log
  axios.interceptors.request.use(
    (config) => {
      // Don't add token to Auth endpoints to avoid circular dependency or unnecessary headers
      if (!config.url.includes('/auth/login') && !config.url.includes('/auth/refresh')) {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
      }
      logger.api.debug(`${config.method?.toUpperCase()} ${config.url}`, {
        method: config.method?.toUpperCase(),
        url: config.url,
      });
      return config;
    },
    (error) => {
      logger.api.error('Request interceptor error', { error: error.message });
      return Promise.reject(error);
    }
  );

  // 2. Intercept Responses to handle 401 (Token Expiry) and log
  axios.interceptors.response.use(
    (response) => {
      logger.api.debug(`Response ${response.status} from ${response.config?.url}`, {
        status: response.status,
        url: response.config?.url,
      });
      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      const status = error.response?.status;
      const url = originalRequest?.url || 'unknown';

      logger.api.warn(`Response ${status || 'NETWORK_ERROR'} from ${url}`, {
        status,
        url,
        message: error.message,
      });

      // If error is 401 and we haven't retried yet
      if (status === 401 && !originalRequest._retry && !originalRequest?.url?.includes('/auth/login')) {
        originalRequest._retry = true;
        logger.auth.info('Access token expired, attempting refresh...');

        try {
          const refreshToken = localStorage.getItem('refreshToken');

          if (!refreshToken) {
            throw new Error("No refresh token available");
          }

          // Call Refresh Endpoint
          const res = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken: refreshToken
          });

          if (res.status === 200) {
            logger.auth.info('Token refresh successful, retrying original request');
            // Save new tokens
            localStorage.setItem('authToken', res.data.accessToken);
            localStorage.setItem('refreshToken', res.data.refreshToken);

            // Update header for original request
            originalRequest.headers['Authorization'] = `Bearer ${res.data.accessToken}`;

            // Retry original request
            return axios(originalRequest);
          }
        } catch (refreshError) {
          logger.auth.error('Session expired - refresh failed, redirecting to login', {
            error: refreshError.message,
          });
          // Clear storage and redirect to login
          localStorage.clear();
          window.location.href = '/login';
        }
      }

      return Promise.reject(error);
    }
  );

  logger.info('Axios interceptors configured');
};